import path from 'path-browserify'
import Toast from 'react-hot-toast';
import { Link, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import {OnboardingSetPassword} from "./onboarding/setPassword";
import {OnboardingFillProfile} from './onboarding/fillProfile';
import { useEffect, useState } from 'react';
import formatISO from 'date-fns/formatISO';
import { useClient, useSession } from '../providers/supabase';
import uniq from 'lodash/uniq';
import { ProfileRow } from '../../types/supabase';
import { OnboardingSetMembershipDetails } from './onboarding/setMembershipDetails';
import { OnboardingAddDependents } from './onboarding/addDependents';
import { useUser } from "../hooks/useUser";
import { OnlineRequired } from './onlineRequired';
import { PostgrestError, User } from '@supabase/supabase-js';
import { present } from '../../lib/util/present';
import { SignOutButton } from '../components/signOutButton';
import { Help } from '../routes/help';
import { NotFound } from './notFound';
import { useMailchimpSignUpHandler } from '../hooks/useMailchimpSignUpHandler';
import Notifications from '../components/notifications';
import { OnboardingFlowState, OnboardingFlowStates, hasCompletedOnboarding } from '../reduxToolkit/membershipSlice';

import './onboarding.scss'

function Onboarding({
  onComplete
} : {
  onComplete: () => void
}) {
  const client = useClient()
  const navigate = useNavigate()
  const location = useLocation()
  const user = useUser()
  const [{}, mailchimpSignUp] = useMailchimpSignUpHandler()
  
  const pathname = path.basename(location.pathname)
  const currentIdx = OnboardingFlowStates.findIndex((p) => p === pathname)

  const [stepsCompleted, setStepsCompleted] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | PostgrestError>()
  const [wait, setWait] = useState(false)

  useEffect(() => {
    if (!user?.id) { throw new Error(`User is not signed in`) }

    client.from('profiles')
      .select('onboarding_steps_completed')
      .eq('id', user.id)
      .then(
        ({ data, error }) => {
          if (error) { setError(error) }
          if (data && data.length > 0) {
            const profile = data[0] as ProfileRow
            setStepsCompleted(profile.onboarding_steps_completed || [])
          }
          setLoading(false)
        },
        (error) => {
          setError(error)
          setLoading(false)
        }
      )
  }, [client, user?.id])

  if (error) {
    if (/NetworkError/.test(error.message)) {
      return <OnlineRequired onConnectionEstablished={() => {
        setError(undefined)
      }} />
    }
    // else
    throw error
  }

  if (!user) {
    throw new Error(`User is not signed in`)
  }

  if (loading) {
    return <div className="row">
      <div className="col-12 col-lg-6 offset-lg-3">
        <div className={`loading-bar`}></div>
      </div>
    </div>
  }

  const onStepComplete = (step: OnboardingFlowState) => {
    if (!user?.id) { throw new Error('Please sign in first') }

    setWait(true)
    const newStepsCompleted = uniq([...(stepsCompleted || []), step])
    setStepsCompleted(newStepsCompleted)

    const completeStep = async () => {
      const { error } = await client.from('profiles').update({
          onboarding_step_completed_at: formatISO(new Date()),
          onboarding_steps_completed: newStepsCompleted
        })
        .eq('id', user.id)
      if (error) { throw error }

      if ((currentIdx + 1) >= OnboardingFlowStates.length) {
        console.log('onboarding complete')
        navigate('')
        mailchimpSignUp(['onboarding-complete'])
        onComplete()
      } else {
        const nextPath = OnboardingFlowStates[currentIdx + 1]
        navigate(nextPath)
      }
    }

    return completeStep()
      .catch((err) => {
        setError(err)
      })
      .finally(() => setWait(false))
  }

  return <div className='App container'>
    <div className="row" style={{ height: '100vh', flexDirection: 'column' }}>
      <div className='col-12'>
        <OnboardingTopNav />
        <Notifications />
      </div>

      <div className="col-12">
        <Routes>
          <Route index element={<OnboardingHome hasCompletedOnboarding={hasCompletedOnboarding({ onboarding_steps_completed: stepsCompleted })} />} />
          <Route path="setPassword" element={<OnboardingSetPassword user={user} onSetPassword={() => onStepComplete('setPassword')} />} />
          <Route path="setMembership" element={<OnboardingSetMembershipDetails user={user} onComplete={() => onStepComplete('setMembership')} />} />
          <Route path="fillProfile" element={<OnboardingFillProfile user={user} onProfileFilled={() => onStepComplete('fillProfile')} />} />
          <Route path="addDependents" element={<OnboardingAddDependents user={user} onComplete={() => onStepComplete('addDependents')} />} />

          <Route path="/help" element={<Help />} />

          <Route path="*" element={<NotFound />} />
        </Routes>
        <div className={`loading-bar ${wait ? 'visible' : 'invisible'}`}></div>
      </div>

      <div className="col-12" style={{marginTop: 'auto'}}>
        <OnboardingFlowProgress currentIdx={currentIdx} />
      </div>
    </div>
  </div>
}

export default Onboarding

function OnboardingHome({
  hasCompletedOnboarding
}: {
  hasCompletedOnboarding?: boolean | null | undefined
}) {
  const session = useSession()
  const [{}, mailchimpSignUp] = useMailchimpSignUpHandler()

  const [tosChecked, setTosChecked] = useState(false)
  const [privacyPolicyChecked, setPrivacyPolicyChecked] = useState(false)

  const canContinue = tosChecked && privacyPolicyChecked

  if (hasCompletedOnboarding) {
    return <div className="row">
      <div className="col-12 col-xl-8 offset-xl-2">
        <h1>Thanks!</h1>
        <p>You are now ready to use the calculator.</p>
        <p><Link to="/">Start tracking your expenses</Link></p>
      </div>
    </div>
  }

  if (!session) {
    throw new Error(`User is not signed in`)
  }
  if (!userMaySignUp(session.user)) {
    return <div className="row">
      <div className="col-12 col-xl-8 offset-xl-2">
        <h1>Sorry!</h1>
        <p>
          Use of this app is currently by invitation only.
          Please sign in using your work email address.
        </p>
        <p>
          If your organization is not yet using HealthShare, you are
          welcome to <MailchimpSignUpLink>sign up for our waiting list!</MailchimpSignUpLink>
        </p>
        <SignOutButton>Sign Out</SignOutButton>
      </div>
    </div>
  }

  return <div className="row mt-4">
    <div className="col-12 col-xl-8 offset-xl-2">
      <h1>Welcome to the HealthShare Reimbursement Tracker!</h1>
      <p>
        Here you can:
        <ul>
          <li>Keep track of your medical expenses</li>
          <li>Get clarity on whether to submit to CHM or your HRA</li>
          <li>Get your forms filled out for you</li>
          <li>Be reminded of important dates and deadlines</li>
        </ul>
      </p>

      <h6>Let's get started!</h6>
      <p>
        <form>
          <div className="form-check" onClick={() => {
              setTosChecked(!tosChecked)
            }}>
            <label className="form-check-label">
              Read and agree to the&nbsp;
              <a href="https://app.termly.io/document/terms-of-service/eb660d7f-36de-4395-91fd-9b213ca3d849" target='_blank' rel="noreferrer">Terms and Conditions</a>
            </label>
            <input className="form-check-input" type="checkbox"
              checked={tosChecked} />
          </div>

          <div className="form-check" onClick={() => {
              setPrivacyPolicyChecked(!privacyPolicyChecked)
            }}>
            <label className="form-check-label">
              Read the&nbsp;
              <a href="https://app.termly.io/document/privacy-policy/bf079259-ff12-4a46-9167-dbb4415f71c1" target='_blank' rel="noreferrer">Privacy Policy</a>
            </label>
            <input className="form-check-input" type="checkbox"
              checked={privacyPolicyChecked} />
          </div>
        </form>
      </p>
      <p>
        <Link to="setPassword" className={`btn btn-link ${!canContinue && 'disabled'}`} aria-disabled
            onClick={() => mailchimpSignUp(['onboarding-started'])}>
          Next step: Create a Password
        </Link>
      </p>
    </div>
  </div>
}

const titles: Record<OnboardingFlowState, string> = {
  setPassword: 'Create a Password',
  setMembership: 'Set Membership Details',
  fillProfile: 'Fill out your Profile',
  addDependents: 'Add Dependents'
}

function OnboardingFlowProgress({ currentIdx }: { currentIdx: number}) {
  const [last, current, next] = [
    OnboardingFlowStates[currentIdx - 1],
    OnboardingFlowStates[currentIdx],
    OnboardingFlowStates[currentIdx + 1]
  ]

  return <div className="onboarding__flow-state row">
    {last ?
      <div className="onboarding__flow-state__step col-4 last">
        {titles[last] || last}
      </div> :
      <div className="col-4" />}

    {current ?
      <div className="onboarding__flow-state__step col-4 current">
        {titles[current] || current}
      </div> :
      <div className="col-4" />}

    {next ?
      <div className="onboarding__flow-state__step col-4 next">
        Next: {titles[next] || next}
      </div> :
      <div className="col-4" />}
  </div>
}

function OnboardingTopNav() {
  return <nav className="navbar navbar-expand-lg top-nav">
    <div className="container-fluid d-none d-md-flex">
      <div className='navbar-brand'>
        <h2>HealthShare</h2>
      </div>

      <div className='nav-item'>
        <Link to='/' className='nav-link'>Onboarding</Link>
      </div>


      <div className='nav-item mt-auto'>
        <SignOutButton className='nav-link'>Sign Out</SignOutButton>
      </div>

      <li className='nav-item'>
        <Link to="help" className='nav-link'>
          <i className="material-icons d-none d-md-inline-block">help</i>
          <span className='d-none d-md-inline'>Help</span>
        </Link>
      </li>
    </div>


    <div className="container-fluid d-flex d-md-none">
      <div className='nav-item'>
        <Link to='/' className='nav-link'>
          <i className='material-icons'>home</i>
        </Link>
      </div>

      <div className='nav-item'>
        <Link to="help" className='nav-link'>
          <i className="material-icons">help</i>
        </Link>
      </div>

      <div className='nav-item'>
        <SignOutButton className='nav-link'>
          <i className='material-icons'>logout</i>
        </SignOutButton>
      </div>
    </div>
</nav>
}

function userMaySignUp(user: User): boolean {
  // If the user was invited by another user, they can sign up
  if (present(user.invited_at)) { return true }
  if (!present(user.email)) { return false }

  // If the user belongs to one of the allowed domains, they can sign up
  if (user.email) {
    const allowedDomains = [
      'austinstone.org',
      'ccchapel.com',
      'citizenschurch.com',
      'citybridgechurch.org',
      'eacamps.org',
      'fwwatermark.org',
      'gracechristian.org',
      'healthsharetech.com',
      'pinecove.com',
      'thehills.org',
      'thevillagechurch.net',
      'watermark.org',
    ]
    const emailDomain = user.email.split('@')[1]
    if (allowedDomains.includes(emailDomain)) { return true }
  }

  return false
}

function MailchimpSignUpLink({ children }: React.PropsWithChildren<{ tags?: [] }>) {
  const [{loading}, mailchimpSignUp] = useMailchimpSignUpHandler({
    onSuccess: () => {
      Toast.success('Thanks for signing up!')
    },
    onError: (err) => {
      Toast.error('Something went wrong!  Please try again later.')
    }
  })
  
  return <button className={`btn btn-link ${loading && 'flicker'}`} onClick={() => mailchimpSignUp(['waitlist'])}>{children}</button>
}
